import React, { useState, useEffect } from 'react';
import { collection, query, where, getDocs, doc, updateDoc, getDoc } from 'firebase/firestore';
import { db } from '../../firebase/config';
import { useAuth } from '../../contexts/AuthContext';
import { FiUser, FiMail, FiCheck, FiX, FiAlertCircle, FiLink, FiChevronDown } from 'react-icons/fi';
import './CoachLinkCard.css';

const CoachLinkCard = ({ onLinked }) => {
  const { currentUser, userProfile } = useAuth();

  const [inviteCode, setInviteCode] = useState('');
  const [invitation, setInvitation] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [accepting, setAccepting] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [linkMethod, setLinkMethod] = useState('invite'); // 'invite' or 'select'

  // Coach selection state
  const [availableCoaches, setAvailableCoaches] = useState([]);
  const [selectedCoach, setSelectedCoach] = useState('');
  const [loadingCoaches, setLoadingCoaches] = useState(false);

  // Fetch available coaches
  useEffect(() => {
    const fetchCoaches = async () => {
      if (!showForm) return;

      setLoadingCoaches(true);
      try {
        const usersRef = collection(db, 'users');
        const coachesQuery = query(usersRef, where('role', '==', 'trainer'));
        const querySnapshot = await getDocs(coachesQuery);

        const coaches = querySnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));

        setAvailableCoaches(coaches);
      } catch (error) {
        console.error('[CoachLinkCard] Error fetching coaches:', error);
      } finally {
        setLoadingCoaches(false);
      }
    };

    fetchCoaches();
  }, [showForm]);

  // Validate and fetch invitation
  const handleValidateCode = async (e) => {
    e.preventDefault();

    if (!inviteCode.trim()) {
      setError('Please enter an invite code');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      console.log('[CoachLinkCard] Validating invite code:', inviteCode);

      const invitationsRef = collection(db, 'clientInvitations');
      const inviteQuery = query(
        invitationsRef,
        where('inviteCode', '==', inviteCode.toUpperCase().trim()),
        where('status', '==', 'pending')
      );

      const querySnapshot = await getDocs(inviteQuery);

      if (querySnapshot.empty) {
        setError('Invalid or expired invite code');
        setLoading(false);
        return;
      }

      const inviteDoc = querySnapshot.docs[0];
      const inviteData = inviteDoc.data();

      // Check if invitation has expired
      const expiresAt = inviteData.expiresAt?.toDate();
      if (expiresAt && expiresAt < new Date()) {
        setError('This invitation has expired');
        setLoading(false);
        return;
      }

      // Check if max uses exceeded
      if (inviteData.usesCount >= inviteData.maxUses) {
        setError('This invitation has reached its maximum uses');
        setLoading(false);
        return;
      }

      setInvitation({
        id: inviteDoc.id,
        ...inviteData
      });

      console.log('[CoachLinkCard] Invitation found:', inviteData);
    } catch (err) {
      console.error('[CoachLinkCard] Error validating code:', err);
      setError('Error validating invite code. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Accept invitation and link to trainer
  const handleAcceptInvitation = async () => {
    if (!currentUser || !invitation) return;

    setAccepting(true);
    setError(null);

    try {
      // Check if user already has a trainer
      const userDocRef = doc(db, 'users', currentUser.uid);
      const userDoc = await getDoc(userDocRef);

      if (userDoc.exists() && userDoc.data().trainerId) {
        setError('You are already linked to a coach. Please contact support to change coaches.');
        setAccepting(false);
        return;
      }

      // Update user's trainer ID and coach name
      await updateDoc(userDocRef, {
        trainerId: invitation.trainerId,
        coachName: invitation.trainerName,
        updatedAt: new Date()
      });

      // Update client profile with trainer ID and coach name
      const clientDocRef = doc(db, 'clients', currentUser.uid);
      const clientDoc = await getDoc(clientDocRef);

      if (clientDoc.exists()) {
        await updateDoc(clientDocRef, {
          trainerId: invitation.trainerId,
          coachName: invitation.trainerName,
          updatedAt: new Date()
        });
      }

      // Update invitation status to approved
      const inviteDocRef = doc(db, 'clientInvitations', invitation.id);
      await updateDoc(inviteDocRef, {
        status: 'approved',
        acceptedAt: new Date(),
        acceptedBy: currentUser.uid,
        usesCount: (invitation.usesCount || 0) + 1
      });

      console.log('[CoachLinkCard] Successfully linked to trainer');

      // Reset form and notify parent
      setInvitation(null);
      setInviteCode('');
      setShowForm(false);

      // Call parent callback to refresh data
      if (onLinked) {
        onLinked(invitation.trainerName);
      }

      alert(`Success! You're now connected to ${invitation.trainerName}`);
    } catch (err) {
      console.error('[CoachLinkCard] Error accepting invitation:', err);
      setError('Error connecting to coach. Please try again.');
    } finally {
      setAccepting(false);
    }
  };

  // Link to selected coach directly
  const handleSelectCoach = async (e) => {
    e.preventDefault();

    if (!selectedCoach) {
      setError('Please select a coach');
      return;
    }

    setAccepting(true);
    setError(null);

    try {
      // Check if user already has a trainer
      const userDocRef = doc(db, 'users', currentUser.uid);
      const userDoc = await getDoc(userDocRef);

      if (userDoc.exists() && userDoc.data().trainerId) {
        setError('You are already linked to a coach. Please contact support to change coaches.');
        setAccepting(false);
        return;
      }

      // Get selected coach details
      const selectedCoachData = availableCoaches.find(coach => coach.id === selectedCoach);
      const coachName = selectedCoachData?.displayName || selectedCoachData?.businessName || selectedCoachData?.email || 'Coach';

      // Update user's trainer ID and coach name
      await updateDoc(userDocRef, {
        trainerId: selectedCoach,
        coachName: coachName,
        updatedAt: new Date()
      });

      // Update client profile with trainer ID and coach name
      const clientDocRef = doc(db, 'clients', currentUser.uid);
      const clientDoc = await getDoc(clientDocRef);

      if (clientDoc.exists()) {
        await updateDoc(clientDocRef, {
          trainerId: selectedCoach,
          coachName: coachName,
          updatedAt: new Date()
        });
      }

      console.log('[CoachLinkCard] Successfully linked to selected coach');

      // Reset form and notify parent
      setSelectedCoach('');
      setShowForm(false);

      // Call parent callback to refresh data
      if (onLinked) {
        onLinked(selectedCoachData?.displayName || selectedCoachData?.businessName || 'Coach');
      }

      alert(`Success! You're now connected to ${selectedCoachData?.displayName || selectedCoachData?.businessName || 'your coach'}`);
    } catch (err) {
      console.error('[CoachLinkCard] Error linking to coach:', err);
      setError('Error connecting to coach. Please try again.');
    } finally {
      setAccepting(false);
    }
  };

  // Cancel and go back
  const handleCancel = () => {
    setInvitation(null);
    setInviteCode('');
    setError(null);
  };

  // If not showing form, show the prompt
  if (!showForm) {
    return (
      <div className="coach-link-card">
        <div className="coach-link-prompt">
          <FiLink className="prompt-icon" />
          <h3>Connect with Your Coach</h3>
          <p>Have an invite code from your coach? Connect now to get started!</p>
          <button className="btn-connect" onClick={() => setShowForm(true)}>
            <FiLink /> Enter Invite Code
          </button>
        </div>
      </div>
    );
  }

  // Show invitation details if found
  if (invitation) {
    return (
      <div className="coach-link-card">
        <div className="invitation-preview">
          <div className="preview-header">
            <div className="success-icon">
              <FiCheck />
            </div>
            <h3>Coach Invitation Found!</h3>
          </div>

          <div className="trainer-details">
            <div className="detail-row">
              <FiUser className="detail-icon" />
              <div className="detail-content">
                <label>Coach Name</label>
                <span>{invitation.trainerName}</span>
              </div>
            </div>

            <div className="detail-row">
              <FiMail className="detail-icon" />
              <div className="detail-content">
                <label>Coach Email</label>
                <span>{invitation.trainerEmail}</span>
              </div>
            </div>

            {invitation.message && (
              <div className="invitation-message">
                <p>"{invitation.message}"</p>
              </div>
            )}
          </div>

          {error && (
            <div className="error-message">
              <FiAlertCircle /> {error}
            </div>
          )}

          <div className="preview-actions">
            <button
              className="btn-accept"
              onClick={handleAcceptInvitation}
              disabled={accepting}
            >
              {accepting ? 'Connecting...' : 'Accept & Connect'}
            </button>
            <button
              className="btn-cancel"
              onClick={handleCancel}
              disabled={accepting}
            >
              Cancel
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Show form with both options
  return (
    <div className="coach-link-card">
      <div className="coach-link-form">
        <div className="form-header">
          <FiLink className="form-icon" />
          <h3>Connect to Your Coach</h3>
          <p>Choose how you'd like to connect</p>
        </div>

        {/* Method Tabs */}
        <div className="link-method-tabs">
          <button
            className={`method-tab ${linkMethod === 'invite' ? 'active' : ''}`}
            onClick={() => {
              setLinkMethod('invite');
              setError(null);
            }}
            type="button"
          >
            <FiLink />
            Invite Code
          </button>
          <button
            className={`method-tab ${linkMethod === 'select' ? 'active' : ''}`}
            onClick={() => {
              setLinkMethod('select');
              setError(null);
            }}
            type="button"
          >
            <FiUser />
            Select Coach
          </button>
        </div>

        {/* Invite Code Method */}
        {linkMethod === 'invite' && (
          <form onSubmit={handleValidateCode}>
            <div className="form-group">
              <label htmlFor="inviteCode">Invite Code</label>
              <input
                type="text"
                id="inviteCode"
                className="invite-input"
                placeholder="Enter 8-character code"
                value={inviteCode}
                onChange={(e) => setInviteCode(e.target.value.toUpperCase())}
                maxLength={8}
                disabled={loading}
              />
              <span className="input-hint">
                The invite code is typically 8 characters (e.g., ABC12345)
              </span>
            </div>

            {error && (
              <div className="error-message">
                <FiAlertCircle /> {error}
              </div>
            )}

            <div className="form-actions">
              <button
                type="button"
                className="btn-cancel"
                onClick={() => {
                  setShowForm(false);
                  setInviteCode('');
                  setError(null);
                }}
                disabled={loading}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="btn-validate"
                disabled={loading || !inviteCode.trim()}
              >
                {loading ? (
                  <>
                    <div className="btn-spinner"></div>
                    Validating...
                  </>
                ) : (
                  <>
                    <FiCheck /> Validate Code
                  </>
                )}
              </button>
            </div>
          </form>
        )}

        {/* Select Coach Method */}
        {linkMethod === 'select' && (
          <form onSubmit={handleSelectCoach}>
            <div className="form-group">
              <label htmlFor="coachSelect">Available Coaches</label>
              {loadingCoaches ? (
                <div className="loading-coaches">
                  <div className="btn-spinner"></div>
                  <span>Loading coaches...</span>
                </div>
              ) : (
                <>
                  <div className="select-wrapper">
                    <select
                      id="coachSelect"
                      className="coach-select"
                      value={selectedCoach}
                      onChange={(e) => setSelectedCoach(e.target.value)}
                      disabled={accepting}
                    >
                      <option value="">-- Select a coach --</option>
                      {availableCoaches.map(coach => (
                        <option key={coach.id} value={coach.id}>
                          {coach.displayName || coach.businessName || coach.email}
                        </option>
                      ))}
                    </select>
                    <FiChevronDown className="select-icon" />
                  </div>
                  <span className="input-hint">
                    {availableCoaches.length === 0
                      ? 'No coaches available at the moment'
                      : `${availableCoaches.length} coach${availableCoaches.length !== 1 ? 'es' : ''} available`}
                  </span>
                </>
              )}
            </div>

            {error && (
              <div className="error-message">
                <FiAlertCircle /> {error}
              </div>
            )}

            <div className="form-actions">
              <button
                type="button"
                className="btn-cancel"
                onClick={() => {
                  setShowForm(false);
                  setSelectedCoach('');
                  setError(null);
                }}
                disabled={accepting}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="btn-validate"
                disabled={accepting || !selectedCoach || loadingCoaches}
              >
                {accepting ? (
                  <>
                    <div className="btn-spinner"></div>
                    Connecting...
                  </>
                ) : (
                  <>
                    <FiCheck /> Connect to Coach
                  </>
                )}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
};

export default CoachLinkCard;
